import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import Message from "../models/Message";

/**
 * ### 安否確認 API 操作クラス
 */
export default class SafetyConfirms {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET SafetyConfirm
   */
  public async get(userStore: LoginUser, safetyConfirmsId: number): Promise<SafetyConfirmsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SafetyConfirm, formatParams);

    const response = await this.commonService.executeGet<SafetyConfirmsGetModel>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET SafetyConfirms
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number; order?: string }): Promise<Array<SafetyConfirmsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SafetyConfirms, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<SafetyConfirmsGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET SafetyConfirmsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SafetyConfirms, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST SafetyConfirms
   */
  public async post(userStore: LoginUser, requestBody: SafetyConfirmsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SafetyConfirms, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH SafetyConfirms
   */
  public async patchFamily(userStore: LoginUser, safetyConfirmsId: number, requestBody: FamilySafetyConfirmsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.FamilySafetyConfirm, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH SafetyConfirms
   */
  public async patchOtherUser(userStore: LoginUser, safetyConfirmsId: number, requestBody: OtherUserSafetyConfirmsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherUserSafetyConfirm, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE SafetyConfirms
   */
  public async delete(userStore: LoginUser, safetyConfirmsId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SafetyConfirm, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, null, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface SafetyConfirmsGetModel {
  /** 施設ID */
  facilityId: number;
  /** 災害ID */
  id: number;
  /** 安否確認名 */
  name: string;
  /** 対応する otherUserMessages ソートキー */
  otherUserMessagesSortKey?: Array<string>;
  /** 施設関係者にLINE送信したか否か */
  sendedOtherUserLine: boolean;
  /** 施設関係者にメール送信したか否か */
  sendedOtherUserMail: boolean;
  /** 施設関係者からの安否確認返信を受け付けるか */
  acceptingOtherUserReply: boolean;
  /** 対応する messages ソートキーを保持する */
  messagesSortKey?: Array<string>;
  /** 家族からの安否確認返信を受け付けるか */
  acceptingFamilyReply: boolean;
  /** 登録ユーザー ID */
  createUserId: string;
  /** 登録ユーザー名 */
  createUserName: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** レコード作成日 */
  created: number;
  /** レコード更新日 */
  updated: number;

  /** 最後に送った家族メッセージ(LINE) */
  latestLineMessage: Message | null;
  /** 最後に送った家族メッセージ(MAIL) */
  latestMailMessage: Message | null;
}

export interface SafetyConfirmsPostModel {
  /** 安否確認名 */
  name: string;
}

export interface FamilySafetyConfirmsPatchModel {
  messagesSortKey: string[] | null;
  acceptingFamilyReply: boolean;
  updated: number;
}

export interface OtherUserSafetyConfirmsPatchModel {
  otherUserMessagesSortKey: string[] | null;
  sendedOtherUserLine: boolean;
  sendedOtherUserMail: boolean;
  acceptingOtherUserReply: boolean;
  updated: number;
}
